import { Combobox } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import type { ChartBinding, ChartBindingValue } from '@/entities/dashboards';

type Props = {
  binding: ChartBinding;
  /** Значения, из которых выбирает пользователь; пусто — их ещё не отобрало наполнение */
  options?: ChartBindingValue[];
  /** Выбранное значение разреза; `null` — разрез не выбран */
  value: ChartBindingValue;
  onChange: (value: ChartBindingValue) => void;
};

/**
 * Переключатель разреза диаграммы в её шапке: величина, которую пользователь выбирает на самом
 * дашборде, не входя в режим редактирования.
 *
 * Значение уходит параметром наполнения, поэтому смена разреза перенаполняет все диаграммы,
 * разрезанные разрезом того же имени, и не трогает остальные. Сам запрос при этом не меняется.
 *
 * Значения разреза бывают известны описанию (месяцы, недели), а бывают отобраны наполнением
 * (консультанты) — тогда до первого ответа выбирать не из чего, но переключатель уже показан:
 * иначе шапка диаграммы прыгала бы при каждом перенаполнении.
 */
export const ChartSliceSwitcher: FC<Props> = ({ binding, options, value, onChange }) => {
  const items = useMemo(
    () =>
      (options ?? []).map((option) => ({
        label: String(option),
        value: String(option),
      })),
    [options],
  );

  // Разрез, которому неоткуда взять значения, выбором не управляет — переключать нечего.
  if (!binding.options?.length && !binding.optionsQuery) return null;

  const handleChange = (selected: unknown) => {
    const raw = selected === undefined || selected === null ? null : String(selected);
    if (raw === null) {
      onChange(null);
      return;
    }
    // Значение возвращается тем же видом, каким его дал разрез: число, ушедшее строкой, бэкенд
    // сравнивал бы с числовой колонкой как текст.
    const original = (options ?? []).find((option) => String(option) === raw);
    onChange(original ?? raw);
  };

  return (
    <Combobox
      value={value === null || value === undefined ? undefined : String(value)}
      onChange={handleChange}
      options={items}
      // Имя разреза стоит подсказкой поля: подпись сверху развела бы шапку по высоте с
      // соседними диаграммами, у которых разреза нет.
      placeholder={binding.name}
      isSearchable={items.length > 10}
      size="sm"
      className="w-40 shrink-0"
    />
  );
};
